// Một ô Bói Đê: form nhập liệu theo input_kind → "Bắt đầu" (trừ phí trọn gói,
// BE lập lá số Tử Vi + Bát Tự + gieo quẻ + AI luận) → kết quả + chat hỏi thêm
// (tính phí theo token như chat thường). Slug "tong-hop" = flow Bói Đê tự do cũ.
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Coins,
  Compass,
  HeartHandshake,
  HelpCircle,
  MoonStar,
  ScrollText,
  Sparkles,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DivinerPicker } from "@/components/diviner-picker";
import { IconChomSao, IconDongXu, IconTuTru } from "@/components/nav-icons";
import { TuviLaSoBoard } from "@/components/tuvi/tuvi-la-so-board";
import { BaziBoard } from "@/components/bazi/bazi-board";
import { HexagramFullBoard } from "@/components/iching/hexagram-full-board";
import {
  BirthInputForm,
  birthValueFromProfile,
  defaultBirthValue,
  isBirthHourSet,
  isBirthValueComplete,
  isBirthValueReadyForChart,
  toPersonInput,
  type BirthFormValue,
} from "@/components/boi-de/birth-input-form";
import { NgayOptionsCards, NgayTotCard } from "@/components/boi-de/ngay-tot-card";
import { CompareResultCard, TimelineResultCard } from "@/components/boi-de/structured-result";
import { ChatPanel, ScaleToFit, TongHopClassic } from "@/components/boi-de/tong-hop-classic";
import { SilkReadingMarkdown } from "@/components/boi-de/silk-reading";
import { InitialReadingRating } from "@/components/boi-de/initial-reading-rating";
import { useRegisterTour } from "@/components/tour/tour-provider";
import {
  formatVnd,
  topicAccent,
  topicDisplayName,
  topicIcon,
} from "@/components/boi-de/topic-style";
import {
  displayName,
  getDefaultDivinerId,
  refreshBoideUser,
  useBoideUser,
} from "@/hooks/use-boide-user";
import { useQuotaStatus } from "@/hooks/use-quota-status";
import {
  getSession,
  listBoiDeTopics,
  startBoiDeReading,
  type BaziChart,
  type BoiDeCompat,
  type BoiDeExtraInput,
  type BoiDeNgayTot,
  type BoiDeReadingKind,
  type BoiDeStartInput,
  type BoiDeStructured,
  type BoiDeTopic,
  type HexagramResult,
  type TuviChart,
} from "@/lib/boide-api";
import { AiWatermark } from "@/components/ai-watermark";

export const Route = createFileRoute("/_authenticated/tong-hop/$topic")({
  // `ngay` / `nguoi` / `hoi`: điền sẵn khi chuyển sang từ trang Xem ngày (ngày đã chọn,
  // ngày sinh + giới tính từng người, câu hỏi) — người dùng chỉ còn bổ sung họ tên, giờ sinh.
  validateSearch: (
    search: Record<string, unknown>,
  ): { session?: string; ngay?: string; nguoi?: string; hoi?: string; viec?: string } => ({
    session: typeof search.session === "string" ? search.session : undefined,
    ngay:
      typeof search.ngay === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.ngay)
        ? search.ngay
        : undefined,
    nguoi: typeof search.nguoi === "string" ? search.nguoi.slice(0, 200) : undefined,
    hoi: typeof search.hoi === "string" ? search.hoi.slice(0, 500) : undefined,
    viec:
      typeof search.viec === "string" && /^[a-z_]{2,40}$/.test(search.viec)
        ? search.viec
        : undefined,
  }),
  component: BoiDeTopicPage,
});

type BoiDeResult = {
  session_id: string;
  reading_md: string;
  question?: string;
  tuvi_charts: TuviChart[];
  bazi_charts: BaziChart[];
  hexagram: HexagramResult | null;
  ngay_tot: BoiDeNgayTot | null;
  compat: BoiDeCompat | null;
  structured: BoiDeStructured | null;
};

type LaBanCta = {
  title: string;
  description: string;
  tab: "bat-trach" | "khi-ngay";
};

const LA_BAN_CTA_BY_TOPIC: Record<string, LaBanCta> = {
  "thang-tien-phong-van": {
    title: "Chọn hướng bàn làm việc hỗ trợ công danh",
    description:
      "Mở La bàn Bát Trạch để xem hướng ngồi và hướng bàn làm việc phù hợp với cung phi của bạn.",
    tab: "bat-trach",
  },
  "ngay-khai-truong": {
    title: "Xem hướng thuận cho ngày khai trương",
    description:
      "Dùng La bàn hướng theo khí ngày để ưu tiên hướng đón khách, xuất hành và bắt đầu công việc.",
    tab: "khi-ngay",
  },
  "ngay-khai-truong-ky-hop-dong": {
    title: "Xem hướng thuận cho ngày khai trương",
    description:
      "Dùng La bàn hướng theo khí ngày để ưu tiên hướng đón khách, xuất hành và bắt đầu công việc.",
    tab: "khi-ngay",
  },
  "hoc-hanh": {
    title: "Chọn hướng bàn học giúp tăng tập trung",
    description:
      "Mở La bàn Bát Trạch để tham khảo hướng kê bàn và hướng ngồi học phù hợp với cung phi.",
    tab: "bat-trach",
  },
};

function BoiDeTopicPage() {
  const { topic: slug } = Route.useParams();
  const { session: sessionFromUrl, ngay, nguoi, hoi, viec } = Route.useSearch();
  const prefill = useMemo(
    () => parseTopicPrefill(ngay, nguoi, hoi, viec),
    [ngay, nguoi, hoi, viec],
  );

  const topics = useQuery({ queryKey: ["boi-de-topics"], queryFn: listBoiDeTopics });
  const topic = (topics.data ?? []).find((t) => t.slug === slug);

  return (
    <main className="desktop-feature-background min-h-0 flex-1 overflow-y-auto overscroll-y-contain pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-0">
      <div className="mx-auto w-full max-w-3xl px-3 py-5 md:px-4 md:py-8">
        <Link
          to="/tong-hop"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Bói Đê
        </Link>

        {slug === "tong-hop" ? (
          <>
            <header className="mb-6 text-center">
              <h1
                className="text-2xl font-bold text-gold-gradient md:text-3xl"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Phiên Bói Đê cũ
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Luồng Bói Đê cũ chỉ còn để mở lại phiên đã xem.
              </p>
            </header>
            {sessionFromUrl ? (
              <TongHopClassic initialSessionId={sessionFromUrl} />
            ) : (
              <Card className="border-gold-soft/40 bg-card/60">
                <CardContent className="space-y-3 py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    Bói Đê đã được thu gọn vào màn Bói nhanh.
                  </p>
                  <Button asChild className="bg-gold-gradient text-primary-foreground">
                    <Link to="/tong-hop">Mở Bói nhanh</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        ) : topics.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="mx-auto h-10 w-2/3 rounded-xl" />
            <Skeleton className="h-72 w-full rounded-2xl" />
          </div>
        ) : !topic ? (
          sessionFromUrl ? (
            // Thẻ đã tắt nhưng user mở lại phiên cũ từ Lịch sử → dựng "ghost topic"
            // từ snapshot metadata để vẫn xem kết quả + chat tiếp được.
            <GhostTopicFlow slug={slug} sessionId={sessionFromUrl} />
          ) : (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Chủ đề không tồn tại hoặc đã tắt.{" "}
              <Link to="/tong-hop" className="text-primary underline">
                Chọn chủ đề khác
              </Link>
            </p>
          )
        ) : (
          <TopicFlow topic={topic} sessionFromUrl={sessionFromUrl} prefill={prefill} />
        )}
      </div>
    </main>
  );
}

// Phiên của thẻ đã ngừng cung cấp: dựng topic "ma" từ metadata.boi_de của phiên.
function GhostTopicFlow({ slug, sessionId }: { slug: string; sessionId: string }) {
  const sessionQ = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => getSession(sessionId),
  });
  if (sessionQ.isLoading) return <Skeleton className="h-72 w-full rounded-2xl" />;
  const meta = sessionQ.data?.metadata as { boi_de?: Record<string, unknown> } | undefined;
  const boiDe = meta?.boi_de as
    | {
        topic_name?: string;
        topic_icon?: string;
        price_charged?: number;
        reading_kind?: string;
      }
    | undefined;
  if (!boiDe) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Chủ đề không tồn tại hoặc đã tắt.{" "}
        <Link to="/tong-hop" className="text-primary underline">
          Chọn chủ đề khác
        </Link>
      </p>
    );
  }
  const ghost: BoiDeTopic = {
    id: 0,
    slug,
    name: boiDe.topic_name ?? slug,
    description: "",
    icon: boiDe.topic_icon ?? "Sparkles",
    price: boiDe.price_charged ?? 0,
    input_kind: "single",
    min_persons: 1,
    max_persons: 1,
    extra_person_price: 0,
    group_key: "khac",
    reading_kind: (boiDe.reading_kind as BoiDeReadingKind) ?? "standard",
    extra_inputs: [],
    ui_labels: {},
    config: {},
    sort_order: 0,
  };
  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-border/40 bg-background/40 px-3 py-2 text-center text-xs text-muted-foreground">
        Chủ đề này đã ngừng cung cấp — bạn vẫn xem lại kết quả và trò chuyện tiếp được.
      </p>
      <TopicFlow topic={ghost} sessionFromUrl={sessionId} />
    </div>
  );
}

function TopicHeader({ topic }: { topic: BoiDeTopic }) {
  const Icon = topicIcon(topic.icon);
  const accent = topicAccent(topic.slug);
  return (
    <header className="mb-5 text-center">
      <span
        className={`mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl ${accent.bg}`}
      >
        <Icon className={`topic-icon--light h-7 w-7 ${accent.text}`} />
      </span>
      <h1
        className="text-2xl font-bold text-gold-gradient md:text-3xl"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {topicDisplayName(topic.slug, topic.name)}
      </h1>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">{topic.description}</p>
    </header>
  );
}

export function TopicFlow({
  topic,
  sessionFromUrl,
  compactProfile = false,
  unifiedShell = false,
  onOpenTemplates,
  prefill,
}: {
  topic: BoiDeTopic;
  sessionFromUrl?: string;
  compactProfile?: boolean;
  unifiedShell?: boolean;
  onOpenTemplates?: () => void;
  prefill?: TopicPrefill | null;
}) {
  const [result, setResult] = useState<BoiDeResult | null>(null);

  // Mở lại từ Lịch sử / reload: dựng kết quả từ snapshot trong metadata phiên.
  const sessionQ = useQuery({
    queryKey: ["session", sessionFromUrl],
    queryFn: () => getSession(sessionFromUrl!),
    enabled: Boolean(sessionFromUrl) && !result,
  });

  const restored: BoiDeResult | null = (() => {
    if (result) return result;
    const meta = sessionQ.data?.metadata;
    if (!sessionFromUrl || !meta?.boi_de) return null;
    const hexes = (meta.hexagrams as HexagramResult[] | undefined) ?? [];
    const ngayTot = meta.boi_de.ngay_tot as BoiDeResult["ngay_tot"] | null;
    return {
      session_id: sessionFromUrl,
      reading_md: meta.boi_de.reading_md ?? "",
      question: meta.boi_de.question ?? "",
      tuvi_charts: (meta.tuvi_snapshots as TuviChart[] | undefined) ?? [],
      bazi_charts: (meta.bazi_snapshots as BaziChart[] | undefined) ?? [],
      hexagram: hexes[0] ?? null,
      // Chế độ tìm ngày: report=null nhưng vẫn có options → vẫn phải khôi phục.
      ngay_tot: ngayTot?.report || ngayTot?.options?.length ? ngayTot : null,
      compat: (meta.boi_de.compat as BoiDeCompat | null) ?? null,
      structured: (meta.boi_de.structured as BoiDeStructured | null) ?? null,
    };
  })();

  if (restored) {
    return <TopicResult topic={topic} result={restored} />;
  }
  if (sessionFromUrl && sessionQ.isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }
  return (
    <TopicStartForm
      topic={topic}
      onDone={setResult}
      compactProfile={compactProfile}
      unifiedShell={unifiedShell}
      onOpenTemplates={onOpenTemplates}
      prefill={prefill}
    />
  );
}

// ─── Màn chờ luận giải ──────────────────────────────────────────────────────

const LOADING_STEPS_CHART = [
  { icon: Sparkles, text: "An sao, lập lá số Tử Vi" },
  { icon: ScrollText, text: "Dựng tứ trụ Bát Tự" },
  { icon: Coins, text: "Gieo quẻ Mai Hoa" },
  { icon: MoonStar, text: "Thầy chiêm nghiệm, luận giải" },
];

const LOADING_STEPS_DATE = [
  { icon: CalendarDays, text: "Tra can chi, tiết khí" },
  { icon: Sparkles, text: "Xét trực, sao hoàng đạo, xung hợp tuổi" },
  { icon: Coins, text: "Gieo quẻ Mai Hoa" },
  { icon: MoonStar, text: "Thầy chiêm nghiệm, luận giải" },
];

const MIN_LOADING_STEP_MS = 1000;
const MAX_LOADING_STEP_MS = 4000;

function createLoadingStepDurations(stepCount: number): number[] {
  return Array.from(
    { length: stepCount },
    () =>
      MIN_LOADING_STEP_MS +
      Math.floor(Math.random() * (MAX_LOADING_STEP_MS - MIN_LOADING_STEP_MS + 1)),
  );
}

function DivinationLoading({
  topic,
  includeHexagram = false,
  stepDurations,
  onSequenceComplete,
}: {
  topic: BoiDeTopic;
  includeHexagram?: boolean;
  stepDurations: number[];
  onSequenceComplete: () => void;
}) {
  const allSteps = topic.input_kind === "date" ? LOADING_STEPS_DATE : LOADING_STEPS_CHART;
  const steps = includeHexagram
    ? allSteps
    : allSteps.filter((step) => step.text !== "Gieo quẻ Mai Hoa");
  const [active, setActive] = useState(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const finishStep = (currentStep: number) => {
      timer = setTimeout(() => {
        if (currentStep >= steps.length - 1) {
          // Nếu BE còn chậm, dòng cuối vẫn giữ trạng thái loading sau callback này.
          onSequenceComplete();
          return;
        }
        const nextStep = currentStep + 1;
        setActive(nextStep);
        finishStep(nextStep);
      }, stepDurations[currentStep]);
    };

    finishStep(0);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [onSequenceComplete, stepDurations, steps.length]);

  const Icon = topicIcon(topic.icon);
  const accent = topicAccent(topic.slug);

  return (
    <Card className="border-gold-soft/40 bg-card/60">
      <CardContent className="flex flex-col items-center gap-5 py-10 text-center md:py-12">
        <div className="relative">
          <span className={`flex h-16 w-16 items-center justify-center rounded-full ${accent.bg}`}>
            <Icon className={`topic-icon--light h-7 w-7 animate-pulse ${accent.text}`} />
          </span>
          <span className="absolute -inset-1.5 animate-spin rounded-full border border-transparent border-t-[#E5B869]/60 [animation-duration:2.5s]" />
        </div>
        <div className="w-full max-w-xs space-y-2 text-left">
          {steps.map((s, i) => (
            <div
              key={i}
              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-xs transition-all duration-500 ${
                i < active
                  ? "border-border/30 bg-background/30 text-muted-foreground"
                  : i === active
                    ? "border-[#E5B869]/40 bg-[#E5B869]/8 text-foreground"
                    : "border-transparent text-muted-foreground/40"
              }`}
            >
              <s.icon
                className={`h-3.5 w-3.5 shrink-0 ${i === active ? "animate-pulse text-[#E5B869]" : ""}`}
              />
              <span className="flex-1">{s.text}</span>
              {i < active ? <span className="text-[#E5B869]">✦</span> : null}
              {i === active ? (
                <span className="flex gap-0.5">
                  <span className="h-1 w-1 animate-bounce rounded-full bg-[#E5B869] [animation-delay:0ms]" />
                  <span className="h-1 w-1 animate-bounce rounded-full bg-[#E5B869] [animation-delay:150ms]" />
                  <span className="h-1 w-1 animate-bounce rounded-full bg-[#E5B869] [animation-delay:300ms]" />
                </span>
              ) : null}
            </div>
          ))}
        </div>
        <p className="max-w-sm font-serif-soft text-xs italic text-muted-foreground">
          Việc luận trọn gói mất khoảng 30–90 giây — xin giữ trang này mở, tịnh tâm chờ thầy.
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Form bắt đầu ───────────────────────────────────────────────────────────

// Nhãn từng ô người + nhãn nút thêm người: ưu tiên ui_labels từ DB (v72),
// fallback theo slug cho DB chưa migrate.
function personLabel(topic: BoiDeTopic, index: number): string {
  const labels = topic.ui_labels ?? {};
  const fromDb = labels.person_labels?.[index];
  if (fromDb) return fromDb;
  if (labels.extra_person_label && index >= (labels.person_labels?.length ?? 1)) {
    const offset = index - (labels.person_labels?.length ?? 1) + 1;
    return `${labels.extra_person_label} ${offset}`;
  }
  const slug = topic.slug;
  if (slug === "tinh-duyen") return index === 0 ? "Bạn" : "Người ấy";
  if (slug === "hon-nhan-gia-dao") return index === 0 ? "Bạn" : "Vợ / chồng";
  if (slug === "hop-tac-lam-an") return index === 0 ? "Bạn" : `Đối tác ${index}`;
  if (slug === "con-cai") return index === 0 ? "Bạn (bố/mẹ)" : `Vợ/chồng hoặc con ${index}`;
  return index === 0 ? "Thông tin của bạn" : `Người ${index + 1}`;
}

// ISO YYYY-MM-DD theo giờ MÁY user (không dùng toISOString — lệch ngày theo UTC).
function localIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return localIso(d);
}

function diffDays(fromIso: string, toIso: string): number {
  return Math.round(
    (new Date(`${toIso}T00:00:00`).getTime() - new Date(`${fromIso}T00:00:00`).getTime()) /
      86400000,
  );
}

/** Dữ liệu điền sẵn khi chuyển từ trang khác (hiện chỉ có Xem ngày). */
export type TopicPrefill = {
  targetDate?: string;
  persons: { ngay: number; thang: number; nam: number; gioi_tinh: "nam" | "nu" }[];
  question?: string;
  /** Loại việc cụ thể — BE chấm theo bảng của việc này thay vì cấu hình thẻ. */
  purposeKind?: string;
};

/** `nguoi` = "YYYY-MM-DD:nam,YYYY-MM-DD:nu" (ngày sinh dương lịch + giới tính). */
function parseTopicPrefill(
  ngay?: string,
  nguoi?: string,
  hoi?: string,
  viec?: string,
): TopicPrefill | null {
  const persons = (nguoi ?? "")
    .split(",")
    .map((part) => {
      const [d, g] = part.split(":");
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d ?? "");
      if (!m) return null;
      return {
        nam: Number(m[1]),
        thang: Number(m[2]),
        ngay: Number(m[3]),
        gioi_tinh: g === "nu" ? ("nu" as const) : ("nam" as const),
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null)
    .slice(0, 5);
  if (!ngay && !persons.length && !hoi && !viec) return null;
  return { targetDate: ngay, persons, question: hoi?.trim() || undefined, purposeKind: viec };
}

function addPersonLabel(topic: BoiDeTopic): string {
  const fromDb = topic.ui_labels?.add_person_label;
  if (fromDb) return fromDb;
  const slug = topic.slug;
  if (slug === "tinh-duyen") return "Thêm người ấy — xem tương hợp đôi lứa";
  if (slug === "hop-tac-lam-an") return "Thêm đối tác";
  if (slug === "con-cai") return "Thêm vợ/chồng hoặc con";
  return "Thêm người";
}

export function TopicStartForm({
  topic,
  onDone,
  compactProfile = false,
  unifiedShell = false,
  onOpenTemplates,
  prefill,
}: {
  topic: BoiDeTopic;
  onDone: (r: BoiDeResult) => void;
  compactProfile?: boolean;
  unifiedShell?: boolean;
  onOpenTemplates?: () => void;
  prefill?: TopicPrefill | null;
}) {
  const navigate = useNavigate();
  const { user, refresh: refreshUser } = useBoideUser();
  // 🔴 SỐ DƯ TIÊU ĐƯỢC = quota gói còn lại hôm nay + ví (BE: `spendable_balance`,
  // v98). Đọc thẳng `user.amount` là dán nhãn "Số dư chưa đủ" lên màn hình của
  // người còn nguyên hạn mức ngày — mà nút vẫn bấm được và BE vẫn trừ quota bình
  // thường, nên nó chỉ là một lời cảnh báo SAI đuổi người dùng đi nạp tiền.
  // Chưa có câu trả lời từ `/subscriptions/me` thì để `null`: thà không hiện dòng
  // số dư một nhịp còn hơn loé cảnh báo sai bằng con số ví.
  const quota = useQuotaStatus();
  const balance = quota.data?.spendable ?? (quota.isError ? (user?.amount ?? null) : null);

  const isDate = topic.input_kind === "date";
  const config = (topic.config ?? {}) as Record<string, unknown>;
  // Thẻ ngày theo nhu cầu (v72): việc định làm có sẵn trong config → ẩn ô nhập.
  const purposeKind = typeof config.purpose_kind === "string" ? config.purpose_kind : undefined;
  const questionRequired = Boolean(config.question_required);
  const extraInputs: BoiDeExtraInput[] = topic.extra_inputs ?? [];

  // Số người linh hoạt theo topic (v70/v72) — áp cả thẻ ngày (cưới hỏi 2–6 người).
  const minPersons = Math.max(1, topic.min_persons ?? 1);
  const configuredMaxPersons = Math.max(minPersons, topic.max_persons ?? minPersons);
  // Hỏi nhanh là luồng linh hoạt: cho ghép tối đa 5 lá số, kể cả khi DB cũ
  // chưa kịp chạy migration nâng max_persons từ 2 lên 5.
  const maxPersons =
    topic.slug === "hoi-nhanh" ? Math.max(5, configuredMaxPersons) : configuredMaxPersons;

  const [persons, setPersons] = useState<BirthFormValue[]>(() => {
    const fromPrefill = (prefill?.persons ?? [])
      .slice(0, maxPersons)
      .map((p) => ({ ...defaultBirthValue(), ...p }));
    return Array.from(
      { length: Math.max(minPersons, fromPrefill.length) },
      (_, i) => fromPrefill[i] ?? defaultBirthValue(),
    );
  });
  const [targetDate, setTargetDate] = useState(prefill?.targetDate ?? "");
  // Chế độ ngày (thẻ kind date): "have" = đã có ngày dự định; "find" = chưa có,
  // nhờ thầy tìm ngày tốt trong khoảng [searchFrom, searchTo] (BE chỉ đề xuất tương lai).
  const [dateMode, setDateMode] = useState<"have" | "find">("have");
  const todayIso = localIso(new Date());
  const [searchFrom, setSearchFrom] = useState(todayIso);
  const [searchTo, setSearchTo] = useState(addDaysIso(todayIso, 30));
  const searchRangeValid = Boolean(searchFrom && searchTo && searchTo >= searchFrom);
  // BE nhận search_days 7–90 — kẹp tại FE để không bị 400.
  const searchDays = searchRangeValid
    ? Math.min(90, Math.max(7, diffDays(searchFrom, searchTo)))
    : 30;
  const quickRange = (days: number) => {
    setSearchFrom(todayIso);
    setSearchTo(addDaysIso(todayIso, days));
  };
  const pickMonth = (val: string) => {
    // Chọn "trong tháng X/năm Y" → cửa sổ = cả tháng đó, kẹp đầu về hôm nay.
    if (!val) return;
    const [y, m] = val.split("-").map(Number);
    if (!y || !m) return;
    const lastIso = localIso(new Date(y, m, 0));
    if (lastIso < todayIso) {
      toast.error("Tháng này đã qua — chọn tháng hiện tại hoặc tương lai.");
      return;
    }
    const firstIso = localIso(new Date(y, m - 1, 1));
    setSearchFrom(firstIso < todayIso ? todayIso : firstIso);
    setSearchTo(lastIso);
  };
  const [purpose, setPurpose] = useState("");
  const [question, setQuestion] = useState(prefill?.question ?? "");
  const [includeHexagram, setIncludeHexagram] = useState(false);
  const loadingStepCount = includeHexagram
    ? LOADING_STEPS_CHART.length
    : LOADING_STEPS_CHART.length - 1;
  const loadingStepDurations = useMemo(
    () => createLoadingStepDurations(loadingStepCount),
    [loadingStepCount],
  );
  // API và chuỗi hiệu ứng chạy độc lập; chỉ mở kết quả khi cả hai đã hoàn tất.
  const [loadingSequenceComplete, setLoadingSequenceComplete] = useState(false);
  const [pendingResponse, setPendingResponse] = useState<Awaited<
    ReturnType<typeof startBoiDeReading>
  > | null>(null);
  const handleLoadingSequenceComplete = useCallback(() => setLoadingSequenceComplete(true), []);
  const [compactEditing, setCompactEditing] = useState(true);
  const [extraAnswers, setExtraAnswers] = useState<Record<string, string>>({});
  const [divinerId, setDivinerId] = useState("cu_an");
  // Chỉ áp thầy mặc định theo profile khi user CHƯA tự chọn — trước đây effect
  // chạy lại mỗi lần object `user` refresh (focus tab, cập nhật ví...) và đè
  // lên thầy vừa chọn tay → "chọn thầy không ăn".
  const divinerPickedRef = useRef(false);
  useEffect(() => {
    if (!divinerPickedRef.current && user?.profile) {
      setDivinerId(getDefaultDivinerId(user.profile));
    }
  }, [user]);
  const pickDiviner = (id: string) => {
    divinerPickedRef.current = true;
    setDivinerId(id);
  };

  // Hồ sơ đã khai ngày sinh → tự điền ô đầu ("Bạn") cho đỡ gõ lại — vẫn sửa được.
  // Chỉ thử 1 lần khi profile về, và chỉ khi ô đầu còn trống (chưa gõ gì) để
  // không đè input tay; `user` refresh lại (focus tab, cập nhật ví) không đè nữa.
  const [prefilledFromProfile, setPrefilledFromProfile] = useState(false);
  const prefillTriedRef = useRef(false);
  useEffect(() => {
    if (prefillTriedRef.current || !user?.profile) return;
    prefillTriedRef.current = true;
    if (persons[0]?.ho_ten.trim()) return;
    const v = birthValueFromProfile(user.profile, displayName(user, ""));
    if (!v) return;
    const fromUrl = prefill?.persons[0];
    if (
      fromUrl &&
      !v.is_lunar &&
      (v.ngay !== fromUrl.ngay || v.thang !== fromUrl.thang || v.nam !== fromUrl.nam)
    ) {
      return;
    }
    setPersons((list) => list.map((p, i) => (i === 0 ? v : p)));
    setPrefilledFromProfile(true);
    if (compactProfile) setCompactEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setPersonAt = (i: number, v: BirthFormValue) =>
    setPersons((list) => list.map((p, idx) => (idx === i ? v : p)));
  const addPerson = () => setPersons((list) => [...list, defaultBirthValue()]);
  const removePersonAt = (i: number) => setPersons((list) => list.filter((_, idx) => idx !== i));
  const setExtraAt = (key: string, value: string) =>
    setExtraAnswers((prev) => ({ ...prev, [key]: value }));

  // Giá = giá gốc (cho min_persons) + phụ phí mỗi người thêm — khớp công thức BE.
  const extraPrice = topic.extra_person_price ?? 0;
  const price = topic.price + Math.max(0, persons.length - minPersons) * extraPrice;

  const extrasComplete = extraInputs.every(
    (spec) => !spec.required || (extraAnswers[spec.key] ?? "").trim().length > 0,
  );
  const dateReady = dateMode === "have" ? Boolean(targetDate) : searchRangeValid;
  const canStart =
    persons.every(isBirthValueReadyForChart) &&
    (!isDate || (dateReady && (purposeKind || purpose.trim().length > 0))) &&
    (!questionRequired || question.trim().length > 0) &&
    extrasComplete;

  const insufficient = balance !== null && balance < price;
  const missingMessage =
    isDate && !dateReady
      ? dateMode === "have"
        ? "Điền họ tên và ngày dự định để bắt đầu."
        : "Điền họ tên và khoảng thời gian tìm ngày để bắt đầu."
      : questionRequired && !question.trim()
        ? "Nhập câu hỏi để thầy có thể luận giải."
        : !extrasComplete
          ? "Điền đủ các mục có dấu * để bắt đầu."
          : // Giờ sinh dựng trụ giờ Bát Tự và cung Thân Tử Vi — thiếu nó thì lá số
            // là phỏng đoán, nên gọi tên đúng thứ còn thiếu thay vì "điền họ tên".
            persons.some((person) => isBirthValueComplete(person) && !isBirthHourSet(person))
            ? persons.length > 1
              ? "Chọn giờ sinh của từng người để bắt đầu."
              : "Chọn giờ sinh để bắt đầu."
            : persons.length > 1
              ? "Điền họ tên và giờ sinh của từng người để bắt đầu."
              : "Điền họ tên và giờ sinh để bắt đầu.";
  const unifiedCtaLabel = canStart
    ? "Xem lời luận"
    : questionRequired && !question.trim()
      ? "Nhập câu hỏi để tiếp tục"
      : "Hoàn tất thông tin để tiếp tục";

  // Tour hướng dẫn form (hệ tour chuẩn của app) — tự bật 1 lần cho user mới.
  // `ready` phải bắt đầu false rồi mới lật true (convention của useRegisterTour):
  // để provider kịp đăng ký steps trước, nếu không auto-start bắn vào closure
  // startTour cũ (steps rỗng) và bị nuốt mất.
  const [tourReady, setTourReady] = useState(false);
  useEffect(() => setTourReady(true), []);
  useRegisterTour("boide-topic", tourReady);

  const start = useMutation({
    mutationFn: () => {
      const input: BoiDeStartInput = {
        persons: persons.map(toPersonInput),
        question: question.trim() || undefined,
        diviner_id: divinerId,
        include_hexagram: includeHexagram,
      };
      if (isDate) {
        if (dateMode === "have") {
          input.target_date = targetDate;
        } else {
          input.search_from = searchFrom;
          input.search_days = searchDays;
        }
        if (purpose.trim()) input.purpose = purpose.trim();
        if (prefill?.purposeKind) input.purpose_kind = prefill.purposeKind;
      }
      if (extraInputs.length) {
        input.extra_answers = Object.fromEntries(
          Object.entries(extraAnswers)
            .map(([k, v]) => [k, v.trim()])
            .filter(([, v]) => v),
        );
      }
      return startBoiDeReading(topic.slug, input);
    },
    onMutate: () => {
      setLoadingSequenceComplete(false);
      setPendingResponse(null);
    },
    onSuccess: setPendingResponse,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không luận giải được"),
    // 🔴 Lượt này VỪA TRỪ TIỀN (quota trước, ví bù phần thiếu). Không đọc lại hồ sơ
    // thì số dư và hạn mức đứng im tới khi người dùng F5 — và `refreshBoideUser()`
    // cũng chính là thứ bắn sự kiện cho `useQuotaStatus`. Đặt ở `onSettled` vì lỗi
    // SAU khi BE đã trừ tiền là có thật (hoàn tiền, timeout).
    onSettled: () => void refreshBoideUser(),
  });

  useEffect(() => {
    if (!loadingSequenceComplete || !pendingResponse) return;

    const res = pendingResponse;
    setPendingResponse(null);
    setLoadingSequenceComplete(false);
    onDone({
      session_id: res.session_id,
      reading_md: res.reading_md,
      question: question.trim(),
      tuvi_charts: res.tuvi_charts,
      bazi_charts: res.bazi_charts,
      hexagram: res.hexagram,
      ngay_tot: res.ngay_tot,
      compat: res.compat,
      structured: res.structured,
    });
    navigate({
      to: "/tong-hop/$topic",
      params: { topic: topic.slug },
      search: { session: res.session_id },
      replace: true,
    });
    void refreshUser();
    toast.success(
      res.price_charged > 0
        ? `Đã luận giải · trừ ${formatVnd(res.price_charged)}`
        : "Đã luận giải xong",
    );
  }, [
    loadingSequenceComplete,
    navigate,
    onDone,
    pendingResponse,
    question,
    refreshUser,
    topic.slug,
  ]);

  const waitingForResult = start.isPending || pendingResponse !== null;
  const SelectedTopicIcon = topicIcon(topic.icon);
  const selectedTopicAccent = topicAccent(topic.slug);

  if (waitingForResult) {
    return (
      <div className="space-y-4">
        <TopicHeader topic={topic} />
        <DivinationLoading
          topic={topic}
          includeHexagram={includeHexagram}
          stepDurations={loadingStepDurations}
          onSequenceComplete={handleLoadingSequenceComplete}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {unifiedShell ? (
        <header className="boi-de-unified-header px-0.5 pb-1 pt-1 md:pt-3">
          <div className="flex items-start gap-3 px-1">
            <div className="min-w-0 pt-0.5">
              <p className="boi-de-unified-eyebrow">BÓI ĐÊ AI</p>
              <h1 className="font-display text-[21px] font-bold leading-7 tracking-[-0.025em] text-foreground md:text-2xl">
                Điều gì đang khiến bạn băn khoăn?
              </h1>
              <p className="mt-1 text-xs leading-5 text-muted-foreground md:text-sm">
                Kể rõ tình huống để thầy luận giải sát với bạn hơn.
              </p>
            </div>
          </div>
          <section className="boi-de-unified-intro mt-3 px-1 py-1">
            <div className="mt-2.5 grid grid-cols-3 gap-1.5" aria-label="Ba lớp luận giải">
              <div className="flex min-w-0 flex-col items-center rounded-xl border border-gold-soft/35 bg-background/55 px-1.5 py-2 text-center">
                <IconChomSao className="size-[18px] text-primary" />
                <strong className="mt-1 min-h-6 text-[9px] leading-3 text-foreground">
                  Lá số Tử Vi
                </strong>
                <span className="text-[8px] text-muted-foreground">Luận vận</span>
              </div>
              <div className="flex min-w-0 flex-col items-center rounded-xl border border-gold-soft/35 bg-background/55 px-1.5 py-2 text-center">
                <IconTuTru className="size-[18px] text-primary" />
                <strong className="mt-1 min-h-6 text-[9px] leading-3 text-foreground">
                  Lá số Bát Tự
                </strong>
                <span className="text-[8px] text-muted-foreground">Mệnh cục</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={includeHexagram}
                aria-label={
                  includeHexagram
                    ? "Bỏ gieo quẻ Kinh Dịch khỏi lời luận"
                    : "Thêm gieo quẻ Kinh Dịch vào lời luận"
                }
                onClick={() => setIncludeHexagram((value) => !value)}
                className={`flex min-w-0 flex-col items-center rounded-xl border px-1.5 py-2 text-center transition ${
                  includeHexagram
                    ? "border-primary/55 bg-primary/[0.07]"
                    : "border-gold-soft/35 bg-background/55 hover:border-primary/55 hover:bg-primary/[0.04]"
                }`}
              >
                <IconDongXu className="size-[18px] text-primary" />
                <strong className="mt-1 min-h-6 text-[9px] leading-3 text-foreground">
                  Kinh Dịch
                </strong>
                <span className="text-[8px] text-muted-foreground">
                  {includeHexagram ? "Đã bật" : "Gieo quẻ"}
                </span>
              </button>
            </div>
            <p className="mt-2 text-[9px] leading-3.5 text-muted-foreground">
              Bói Đê AI đối chiếu lá số Tử Vi và lá số Bát Tự; khi bật Kinh Dịch, quẻ được tổng hợp
              vào cùng một lời luận.
            </p>
          </section>
          <label
            className="mt-3 block rounded-[24px] border border-gold-soft/60 bg-card p-3.5 text-left shadow-[0_14px_40px_rgba(87,59,17,0.09)] transition focus-within:border-primary/75 focus-within:shadow-[0_18px_46px_rgba(181,118,18,0.14)] md:p-5"
            data-tour="boide-question"
          >
            <span className="mb-1.5 block text-xs font-semibold text-foreground">
              Câu hỏi của bạn
              {questionRequired ? <span className="ml-1 text-primary">*</span> : null}
            </span>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="min-h-[84px] w-full resize-none bg-transparent text-[15px] leading-6 text-foreground outline-none placeholder:text-muted-foreground/80 md:min-h-[112px] md:text-base"
              placeholder="Ví dụ: Tôi đang phân vân có nên đổi công việc trong thời gian này không?"
              maxLength={500}
            />
            <span className="block text-right text-[10px] tabular-nums text-muted-foreground/60">
              {question.length}/500
            </span>
          </label>
          <button
            type="button"
            onClick={onOpenTemplates}
            className="group mt-3 flex w-full items-center gap-3 rounded-[20px] border border-primary/45 bg-primary/[0.055] px-3.5 py-3.5 text-left shadow-[0_10px_28px_rgba(123,82,18,0.09)] transition hover:-translate-y-0.5 hover:border-primary/70 hover:bg-primary/[0.08] hover:shadow-[0_14px_32px_rgba(123,82,18,0.13)]"
          >
            <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/25 bg-card">
              <SelectedTopicIcon
                className={`topic-icon--light size-[22px] ${selectedTopicAccent.text}`}
                strokeWidth={1.8}
              />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[9px] font-bold uppercase tracking-[0.13em] text-primary">
                Chủ đề luận giải
              </span>
              <span className="mt-0.5 block truncate text-sm font-semibold text-foreground">
                {topicDisplayName(topic.slug, topic.name)}
              </span>
              <span className="mt-0.5 block truncate text-[10px] text-muted-foreground">
                Tình duyên · Sự nghiệp · Tài lộc…
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-1 text-[10px] font-semibold text-primary">
              <span className="hidden min-[390px]:inline">Thay đổi</span>
              <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
            </span>
          </button>
        </header>
      ) : compactProfile ? (
        <header className="mb-1 rounded-2xl border border-gold-soft/40 bg-card/50 p-4 text-left backdrop-blur md:p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-serif-soft text-[11px] italic tracking-[0.18em] text-[#E5B869]/75">
                ✦ THIÊN CƠ TRONG TẦM TAY ✦
              </p>
              <h1
                className="mt-1 text-2xl font-bold text-gold-gradient md:text-3xl"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Bói Đê
              </h1>
              <p className="mt-1 text-xs text-muted-foreground">
                Hỏi một điều đang khiến bạn băn khoăn.
              </p>
            </div>
            <span className="rounded-full border border-gold-soft/40 bg-background/40 px-2.5 py-1 text-[10px] text-muted-foreground">
              {topicDisplayName(topic.slug, topic.name)}
            </span>
          </div>
        </header>
      ) : (
        <TopicHeader topic={topic} />
      )}

      {compactProfile && !unifiedShell ? (
        <div
          className="rounded-2xl border border-gold-soft/50 bg-card/65 p-4 shadow-[0_12px_32px_rgba(3,12,25,0.12)] backdrop-blur md:p-5"
          data-tour="boide-question"
        >
          <label className="block">
            <span className="mb-2 block font-serif-soft text-xl text-foreground">
              Bạn đang băn khoăn điều gì?
            </span>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="input-mystic min-h-[124px] w-full resize-none bg-background/70 p-3.5 text-base leading-7 shadow-inner focus:border-primary/70 focus:ring-2 focus:ring-primary/15"
              placeholder="Nhập một câu hỏi cụ thể…"
              maxLength={500}
            />
            <span className="mt-1 block text-right text-[10px] text-muted-foreground">
              {question.length}/500
            </span>
          </label>
        </div>
      ) : null}

      <div data-tour="boide-person-form">
        {compactProfile &&
        !compactEditing &&
        persons.length === 1 &&
        isBirthValueReadyForChart(persons[0]) ? (
          unifiedShell ? null : (
            <div className="rounded-2xl border border-gold-soft/40 bg-card/50 p-4 backdrop-blur">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground">Thông tin người xem</p>
                  <p className="mt-1 truncate text-sm font-medium text-foreground">
                    {persons[0].ho_ten}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {String(persons[0].ngay).padStart(2, "0")}/
                    {String(persons[0].thang).padStart(2, "0")}/{persons[0].nam} ·{" "}
                    {persons[0].gio < 0
                      ? "Chưa rõ giờ"
                      : `${String(persons[0].gio).padStart(2, "0")}:00`}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCompactEditing(true)}
                >
                  Sửa
                </Button>
              </div>
            </div>
          )
        ) : persons.length > 1 ? (
          <div className="grid gap-3 md:grid-cols-2">
            {persons.map((p, i) => (
              <BirthInputForm
                key={i}
                title={personLabel(topic, i)}
                value={p}
                onChange={(v) => setPersonAt(i, v)}
                onRemove={i >= minPersons ? () => removePersonAt(i) : undefined}
              />
            ))}
          </div>
        ) : (
          <BirthInputForm
            title={
              isDate && maxPersons === 1
                ? "Người xem ngày (xét xung hợp tuổi)"
                : personLabel(topic, 0)
            }
            value={persons[0]}
            onChange={(v) => setPersonAt(0, v)}
          />
        )}
        {compactProfile &&
        compactEditing &&
        persons.length === 1 &&
        isBirthValueReadyForChart(persons[0]) ? (
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setCompactEditing(false)}
            >
              Lưu thông tin
            </Button>
          </div>
        ) : null}
        {prefilledFromProfile && !unifiedShell ? (
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            Đã tự điền thông tin của bạn từ hồ sơ — kiểm tra và chỉnh sửa nếu cần.
          </p>
        ) : null}
      </div>

      {!unifiedShell && maxPersons > minPersons && persons.length < maxPersons ? (
        <button
          type="button"
          onClick={addPerson}
          className="group flex w-full items-center justify-center gap-2 px-2 py-2 text-sm font-medium text-primary transition hover:text-primary/80"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {topic.slug === "hoi-nhanh"
            ? "Kết nối thêm lá số người tiếp theo"
            : maxPersons === 2
              ? "Kết nối thêm lá số người thứ hai"
              : addPersonLabel(topic)}
          {topic.slug === "hoi-nhanh" ? (
            <span className="text-[11px] font-normal text-muted-foreground">
              ({persons.length}/5)
            </span>
          ) : null}
          {extraPrice > 0 ? (
            <span className="flex h-7 min-w-7 items-center justify-center rounded-full border border-primary/40 bg-primary/[0.07] px-1.5 text-[10px] font-semibold text-primary shadow-sm">
              +{Math.max(1, Math.round(extraPrice / 1000))}K
            </span>
          ) : null}
        </button>
      ) : !unifiedShell && topic.slug === "hoi-nhanh" && persons.length >= 5 ? (
        <p className="text-center text-[11px] text-muted-foreground">Đã thêm tối đa 5 lá số.</p>
      ) : null}

      {isDate ? (
        <div className="space-y-3 rounded-2xl border border-gold-soft/40 bg-card/50 p-4 backdrop-blur">
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-input bg-background/40 p-1">
            {(["have", "find"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setDateMode(mode)}
                className={`rounded-lg py-1.5 text-xs font-medium transition ${
                  dateMode === mode
                    ? "bg-gold-gradient text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {mode === "have" ? "Đã có ngày dự định" : "Nhờ thầy tìm ngày tốt"}
              </button>
            ))}
          </div>

          {dateMode === "have" ? (
            <label className="block">
              <span className="mb-1.5 block text-xs text-muted-foreground">Ngày dự định</span>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="input-mystic w-full"
              />
            </label>
          ) : (
            <div className="space-y-2.5">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs text-muted-foreground">Tìm từ ngày</span>
                  <input
                    type="date"
                    min={todayIso}
                    value={searchFrom}
                    onChange={(e) => setSearchFrom(e.target.value)}
                    className="input-mystic w-full"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-xs text-muted-foreground">Đến ngày</span>
                  <input
                    type="date"
                    min={searchFrom || todayIso}
                    value={searchTo}
                    onChange={(e) => setSearchTo(e.target.value)}
                    className="input-mystic w-full"
                  />
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {(
                  [
                    ["2 tuần tới", 14],
                    ["1 tháng tới", 30],
                    ["3 tháng tới", 90],
                  ] as const
                ).map(([label, days]) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => quickRange(days)}
                    className="rounded-full border border-gold-soft/40 bg-background/40 px-2.5 py-1 text-[11px] text-muted-foreground transition hover:border-primary/60 hover:text-foreground"
                  >
                    {label}
                  </button>
                ))}
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  hoặc trong tháng
                  <input
                    type="month"
                    min={todayIso.slice(0, 7)}
                    onChange={(e) => pickMonth(e.target.value)}
                    className="input-mystic px-2 py-1 text-[11px]"
                  />
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {searchRangeValid
                  ? `Thầy sẽ chọn 3 ngày đẹp nhất trong ${searchDays} ngày này (chỉ tính từ hôm nay trở đi, tối đa 90 ngày).`
                  : "Chọn khoảng thời gian hợp lệ — ngày kết thúc phải sau ngày bắt đầu."}
              </p>
            </div>
          )}

          {purposeKind ? null : (
            <label className="block">
              <span className="mb-1.5 block text-xs text-muted-foreground">
                Việc định làm (cưới hỏi, khai trương, ký hợp đồng…)
              </span>
              <input
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="input-mystic w-full"
                placeholder="VD: khai trương cửa hàng"
                maxLength={300}
              />
            </label>
          )}
        </div>
      ) : null}

      {extraInputs.length > 0 ? (
        <div className="space-y-3 rounded-2xl border border-gold-soft/40 bg-card/50 p-4 backdrop-blur">
          {extraInputs.map((spec) => (
            <label key={spec.key} className="block">
              <span className="mb-1.5 block text-xs text-muted-foreground">
                {spec.label}
                {spec.required ? <span className="text-red-400"> *</span> : null}
              </span>
              {spec.type === "textarea" ? (
                <textarea
                  value={extraAnswers[spec.key] ?? ""}
                  onChange={(e) => setExtraAt(spec.key, e.target.value)}
                  className="input-mystic min-h-[72px] w-full resize-y"
                  placeholder={spec.placeholder}
                  maxLength={spec.max_length ?? 500}
                />
              ) : spec.type === "select" ? (
                <select
                  value={extraAnswers[spec.key] ?? ""}
                  onChange={(e) => setExtraAt(spec.key, e.target.value)}
                  className="input-mystic w-full"
                >
                  <option value="">— Chọn —</option>
                  {(spec.options ?? []).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : spec.type === "date" ? (
                <input
                  type="date"
                  value={extraAnswers[spec.key] ?? ""}
                  onChange={(e) => setExtraAt(spec.key, e.target.value)}
                  className="input-mystic w-full"
                />
              ) : (
                <input
                  value={extraAnswers[spec.key] ?? ""}
                  onChange={(e) => setExtraAt(spec.key, e.target.value)}
                  className="input-mystic w-full"
                  placeholder={spec.placeholder}
                  maxLength={spec.max_length ?? 300}
                />
              )}
            </label>
          ))}
        </div>
      ) : null}

      <div
        className={`space-y-3 ${unifiedShell ? "rounded-[22px] bg-card/55 px-3 py-3.5" : "rounded-2xl border border-gold-soft/40 bg-card/50 p-4 backdrop-blur"}`}
        data-tour="boide-question"
      >
        {unifiedShell &&
        compactProfile &&
        !compactEditing &&
        persons.length === 1 &&
        isBirthValueReadyForChart(persons[0]) ? (
          <div className="border-b border-gold-soft/30 pb-3.5">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Thông tin dùng để luận giải
              </p>
              {prefilledFromProfile ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary">
                  <CheckCircle2 className="size-3.5" />
                  Từ hồ sơ
                </span>
              ) : null}
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-foreground">{persons[0].ho_ten}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {String(persons[0].ngay).padStart(2, "0")}/
                  {String(persons[0].thang).padStart(2, "0")}/{persons[0].nam} ·{" "}
                  {persons[0].gio < 0
                    ? "Chưa rõ giờ sinh"
                    : `${String(persons[0].gio).padStart(2, "0")}:00`}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCompactEditing(true)}
                className="h-8 shrink-0 rounded-full px-3 text-[11px]"
              >
                Chỉnh sửa
              </Button>
            </div>
            {maxPersons > minPersons && persons.length < maxPersons ? (
              <button
                type="button"
                onClick={addPerson}
                className="mt-3 flex w-full items-center justify-between gap-3 rounded-2xl border border-dashed border-primary/35 bg-primary/[0.04] px-3 py-2.5 text-left transition hover:border-primary/65 hover:bg-primary/[0.07]"
              >
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-foreground">
                    Thêm người để đối chiếu
                  </span>
                  <span className="mt-0.5 block text-[10px] text-muted-foreground">
                    Đã thêm {persons.length}/{maxPersons} người
                    {extraPrice > 0 ? ` · +${formatVnd(extraPrice)}/người` : ""}
                  </span>
                </span>
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <ArrowRight className="size-3.5" />
                </span>
              </button>
            ) : null}
          </div>
        ) : null}
        {unifiedShell && persons.length > 1 && persons.length < maxPersons ? (
          <button
            type="button"
            onClick={addPerson}
            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-dashed border-primary/35 bg-primary/[0.04] px-3 py-2.5 text-left transition hover:border-primary/65 hover:bg-primary/[0.07]"
          >
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-foreground">
                Thêm người để đối chiếu
              </span>
              <span className="mt-0.5 block text-[10px] text-muted-foreground">
                Đã thêm {persons.length}/{maxPersons} người
                {extraPrice > 0 ? ` · +${formatVnd(extraPrice)}/người` : ""}
              </span>
            </span>
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <ArrowRight className="size-3.5" />
            </span>
          </button>
        ) : null}
        {unifiedShell && persons.length >= maxPersons && maxPersons > minPersons ? (
          <p className="text-center text-[10px] text-muted-foreground">
            Đã thêm tối đa {maxPersons} người để đối chiếu.
          </p>
        ) : null}
        {!compactProfile ? (
          <label className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <HelpCircle className="h-3.5 w-3.5" />
              {questionRequired ? (
                <>
                  Câu hỏi của bạn <span className="text-red-400">*</span> (thầy trả lời thẳng câu
                  này)
                </>
              ) : (
                "Điều bạn đang băn khoăn (không bắt buộc — giúp thầy luận sát hơn)"
              )}
            </span>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="input-mystic w-full"
              placeholder={`VD: ${topic.name.toLowerCase()} năm nay của tôi thế nào?`}
              maxLength={500}
            />
          </label>
        ) : null}
        {unifiedShell ? (
          <div className="pt-0.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Cách luận giải
            </p>
          </div>
        ) : null}
        <div
          className={
            unifiedShell ? "grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end" : ""
          }
        >
          <span className="mb-1.5 block text-xs font-medium text-foreground">Thầy luận giải</span>
          <DivinerPicker value={divinerId} onChange={pickDiviner} compact={unifiedShell} />
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={includeHexagram}
          onClick={() => setIncludeHexagram((v) => !v)}
          className={`flex w-full items-center justify-between gap-3 text-left transition ${unifiedShell ? "rounded-2xl border border-border/55 bg-background/45 px-3 py-3 hover:border-primary/50" : "rounded-xl border border-border/40 bg-background/35 px-1 py-3 hover:border-primary/50"}`}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Coins className="h-4 w-4 shrink-0 text-primary" />
            <span>
              <span className="block text-xs font-medium text-foreground">Gieo quẻ Kinh Dịch</span>
              <span className="block text-[11px] text-muted-foreground">
                Thêm một góc nhìn · Không đổi giá
              </span>
            </span>
          </span>
          <span
            className={`relative h-6 w-11 shrink-0 rounded-full p-0.5 transition ${includeHexagram ? "bg-gold-gradient shadow-gold" : "bg-muted"}`}
          >
            <span
              className={`block h-5 w-5 rounded-full bg-background shadow-sm transition-transform ${includeHexagram ? "translate-x-5" : "translate-x-0"}`}
            />
          </span>
        </button>
      </div>

      <Card
        className={
          unifiedShell
            ? "overflow-hidden rounded-[22px] border border-gold-soft/60 bg-card shadow-[0_12px_34px_rgba(45,29,8,0.12)]"
            : "overflow-hidden border-gold-soft/50 bg-card/70"
        }
        data-tour="boide-start"
      >
        <CardContent className={unifiedShell ? "space-y-2 p-2.5" : "space-y-3 py-4"}>
          {unifiedShell ? (
            <div className="flex items-center justify-between gap-3 px-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Tổng chi phí
                {insufficient ? (
                  <span className="ml-1 normal-case tracking-normal text-destructive">
                    · Số dư chưa đủ
                  </span>
                ) : null}
              </p>
              <p
                className={`shrink-0 text-base font-bold tabular-nums ${insufficient ? "text-destructive" : "text-primary"}`}
              >
                {formatVnd(price)}
              </p>
            </div>
          ) : null}
          {!unifiedShell ? (
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Coins className="h-4 w-4 text-primary" /> Trọn gói lá số + lời luận
              </span>
              <span className="text-lg font-bold text-gold-gradient">{formatVnd(price)}</span>
            </div>
          ) : null}
          {persons.length > minPersons ? (
            <p className="text-right text-[11px] text-muted-foreground">
              {formatVnd(topic.price)} + {persons.length - minPersons} người thêm ×{" "}
              {formatVnd(extraPrice)}
            </p>
          ) : null}
          {balance !== null && !unifiedShell ? (
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Wallet className="h-3.5 w-3.5" /> Số dư khả dụng
              </span>
              <span className={insufficient ? "font-medium text-red-400" : "text-muted-foreground"}>
                {formatVnd(balance)}
                {insufficient ? " — chưa đủ" : ""}
              </span>
            </div>
          ) : null}
          <Button
            onClick={() => start.mutate()}
            disabled={!canStart || start.isPending}
            size="lg"
            className={`w-full bg-gold-gradient text-base font-semibold text-primary-foreground shadow-gold ${unifiedShell ? "h-11 rounded-2xl tracking-[-0.01em]" : "uppercase tracking-wide"}`}
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {unifiedShell ? unifiedCtaLabel : `Bắt đầu xem — ${formatVnd(price)}`}
          </Button>
          {!canStart ? (
            <p className="text-center text-[11px] text-muted-foreground">{missingMessage}</p>
          ) : !unifiedShell ? (
            <p className="text-center text-[11px] text-muted-foreground">
              Sau lời luận, hỏi thêm thoải mái — tính phí 1đ/chữ (token) như chat thường.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Kết quả ────────────────────────────────────────────────────────────────

type ResultDetail =
  | { kind: "tuvi"; index: number }
  | { kind: "bazi"; index: number }
  | { kind: "hexagram" }
  | null;

function ResultDetailDialog({
  detail,
  result,
  onClose,
}: {
  detail: ResultDetail;
  result: BoiDeResult;
  onClose: () => void;
}) {
  if (!detail) return null;
  const title =
    detail.kind === "tuvi"
      ? `Lá số Tử Vi — ${result.tuvi_charts[detail.index]?.ho_ten ?? ""}`
      : detail.kind === "bazi"
        ? `Lá số Bát Tự — ${result.bazi_charts[detail.index]?.ho_ten ?? ""}`
        : `Quẻ Mai Hoa — ${result.hexagram?.que_chinh?.ten ?? ""}`;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!left-0 !top-0 flex h-[100dvh] w-screen !max-w-none !translate-x-0 !translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:!left-1/2 sm:!top-1/2 sm:h-[90vh] sm:w-[min(1100px,94vw)] sm:!max-w-none sm:!-translate-x-1/2 sm:!-translate-y-1/2 sm:rounded-2xl">
        <DialogHeader className="shrink-0 border-b border-border/40 px-4 py-3 pr-12 text-left">
          <DialogTitle className="truncate text-base text-foreground md:text-lg">
            {title}
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">Vuốt để xem toàn bộ nội dung</p>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain p-2 pb-20 md:p-4">
          {detail.kind === "tuvi" && result.tuvi_charts[detail.index] ? (
            <ScaleToFit>
              <TuviLaSoBoard
                apiChart={{
                  ...result.tuvi_charts[detail.index],
                  id: result.tuvi_charts[detail.index].id ?? `boi-de-tuvi-${detail.index}`,
                }}
              />
            </ScaleToFit>
          ) : null}
          {detail.kind === "bazi" && result.bazi_charts[detail.index] ? (
            <ScaleToFit>
              <BaziBoard
                chart={{
                  ...result.bazi_charts[detail.index],
                  id: result.bazi_charts[detail.index].id ?? `boi-de-bazi-${detail.index}`,
                }}
              />
            </ScaleToFit>
          ) : null}
          {detail.kind === "hexagram" && result.hexagram ? (
            <ScaleToFit>
              <HexagramFullBoard hex={result.hexagram} />
            </ScaleToFit>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TopicResult({ topic, result }: { topic: BoiDeTopic; result: BoiDeResult }) {
  const [detail, setDetail] = useState<ResultDetail>(null);
  const compatBand = result.compat?.band?.label;
  const laBanCta = LA_BAN_CTA_BY_TOPIC[topic.slug];
  const hasEvidence =
    result.tuvi_charts.length > 0 || result.bazi_charts.length > 0 || Boolean(result.hexagram);
  return (
    <div className="iching-info-panel rounded-[28px] border border-gold-soft/25 p-2 shadow-[0_24px_70px_-38px_rgba(74,50,16,0.25)] sm:p-4 md:p-6">
      {/* Khoảng chừa cho ô nhập cố định do ChatPanel tự giữ (spacer bên trong),
          ở đây chỉ cần padding thường — trước cộng dồn pb-28 + pb-24 của trang
          nên dưới lời luận thừa hơn 200px giấy lụa trống. */}
      <article className="silk-paper relative mx-auto max-w-2xl rounded-md p-4 sm:p-6 lg:p-10">
        <div className="absolute right-4 top-4 animate-seal-stamp">
          <div className="seal-chop">
            <span className="seal-chop-text">印</span>
          </div>
        </div>

        <header className="relative px-8 text-center">
          <span className="font-display text-[9px] uppercase tracking-[0.32em] text-primary/80">
            {topicDisplayName(topic.slug, topic.name)}
          </span>
          <h1 className="mt-4 font-serif-soft text-3xl italic text-gold-gradient drop-shadow-[0_2px_8px_rgba(229,184,105,0.25)] md:text-4xl">
            Lời luận của thầy
          </h1>
          <p className="mt-3 font-serif-soft text-sm italic text-foreground/75 md:text-base">
            Tử Vi · Bát Tự{result.hexagram ? " · Kinh Dịch" : ""}
          </p>
          <div className="mx-auto mt-4 h-px w-32 bg-gold-gradient" />
        </header>

        <div className="mt-5 flex flex-wrap items-center justify-center gap-1.5">
          {result.compat?.score != null ? (
            <Badge className="gap-1 bg-gold-gradient/15 text-xs text-foreground">
              <HeartHandshake className="h-3.5 w-3.5 text-primary" />
              Tương hợp {result.compat.score}/100{compatBand ? ` · ${compatBand}` : ""}
            </Badge>
          ) : null}
          {result.hexagram?.que_chinh?.ten ? (
            <Badge className="gap-1 bg-gold-gradient/15 text-xs text-foreground">
              <Coins className="h-3.5 w-3.5 text-primary" />
              Quẻ {result.hexagram.que_chinh.ten}
            </Badge>
          ) : null}
          {result.ngay_tot?.report ? (
            <Badge className="gap-1 bg-gold-gradient/15 text-xs text-foreground">
              <CalendarDays className="h-3.5 w-3.5 text-primary" />
              {result.ngay_tot.report.verdict} · {result.ngay_tot.report.score}/100
            </Badge>
          ) : result.ngay_tot?.options?.length ? (
            <Badge className="gap-1 bg-gold-gradient/15 text-xs text-foreground">
              <CalendarDays className="h-3.5 w-3.5 text-primary" />
              Tìm ngày tốt · {result.ngay_tot.options.length} phương án
            </Badge>
          ) : null}
        </div>

        <div className="mt-6 space-y-4">
          {result.ngay_tot?.options?.length ? (
            <NgayOptionsCards options={result.ngay_tot.options} />
          ) : null}

          {result.ngay_tot?.report ? (
            <NgayTotCard
              report={result.ngay_tot.report}
              alternatives={result.ngay_tot.alternatives ?? []}
            />
          ) : null}

          {result.structured?.kind === "compare" ? (
            <CompareResultCard compare={result.structured} />
          ) : null}
          {result.structured?.kind === "timeline" ? (
            <TimelineResultCard timeline={result.structured} />
          ) : null}
          {result.structured ? <AiWatermark /> : null}
        </div>

        {hasEvidence ? (
          <section className="mt-6 border-y border-gold-soft/35 py-3" aria-label="Căn cứ luận giải">
            <p className="px-2 pb-1 pt-1 text-center font-display text-[9px] uppercase tracking-[0.28em] text-primary/75">
              Căn cứ luận giải
            </p>
            {result.tuvi_charts.map((c, i) => (
              <button
                key={`tuvi-${i}`}
                type="button"
                onClick={() => setDetail({ kind: "tuvi", index: i })}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition hover:bg-background/45"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Sparkles className="h-4 w-4 text-primary" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">Lá số Tử Vi</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {c.ho_ten} · 12 cung
                  </span>
                </span>
                <span className="text-xs font-medium text-primary">Xem</span>
              </button>
            ))}
            {result.bazi_charts.map((c, i) => (
              <button
                key={`bazi-${i}`}
                type="button"
                onClick={() => setDetail({ kind: "bazi", index: i })}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition hover:bg-background/45"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <ScrollText className="h-4 w-4 text-primary" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">Lá số Bát Tự</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {c.ho_ten} · Tứ trụ
                  </span>
                </span>
                <span className="text-xs font-medium text-primary">Xem</span>
              </button>
            ))}
            {result.hexagram ? (
              <button
                type="button"
                onClick={() => setDetail({ kind: "hexagram" })}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition hover:bg-background/45"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Coins className="h-4 w-4 text-primary" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">Quẻ Mai Hoa</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {result.hexagram.que_chinh?.ten ?? "Quẻ đã gieo"}
                  </span>
                </span>
                <span className="text-xs font-medium text-primary">Xem</span>
              </button>
            ) : null}
          </section>
        ) : null}

        {result.question ? (
          <div className="mt-6 rounded-md border border-gold-soft/50 bg-card/70 p-3 text-center shadow-sm dark:bg-background/30 md:p-4">
            <span className="font-display text-[9px] tracking-[0.3em] text-primary/80">
              TÂM SỰ NGƯỜI HỎI
            </span>
            <p className="mt-2 font-serif-soft text-base italic leading-7 text-foreground/90">
              “{result.question}”
            </p>
          </div>
        ) : null}

        {result.reading_md ? (
          <section className="mt-8" aria-label="Lời luận ban đầu">
            <SilkReadingMarkdown body={result.reading_md} />
            <AiWatermark />
            <InitialReadingRating
              sessionId={result.session_id}
              className="mt-4 border-t border-gold-soft/25 pt-3"
            />
          </section>
        ) : null}

        {laBanCta ? (
          <aside className="mt-6 rounded-2xl border border-primary/35 bg-primary/[0.065] p-4 shadow-[0_12px_32px_rgba(83,55,18,0.09)] sm:flex sm:items-center sm:gap-4">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-primary/25 bg-card text-primary shadow-sm">
              <Compass className="size-5" />
            </span>
            <div className="mt-3 min-w-0 flex-1 sm:mt-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">
                La bàn phong thuỷ
              </p>
              <h2 className="mt-1 font-display text-base font-bold leading-snug text-foreground">
                {laBanCta.title}
              </h2>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{laBanCta.description}</p>
            </div>
            <Link
              to="/tong-hop/la-ban"
              search={{ tab: laBanCta.tab }}
              className="mt-4 inline-flex min-h-10 w-full shrink-0 items-center justify-center gap-1.5 rounded-full bg-primary px-4 text-xs font-bold text-primary-foreground shadow-sm transition hover:brightness-105 active:scale-[0.98] sm:mt-0 sm:w-auto"
            >
              Mở La bàn
              <ArrowRight className="size-3.5" />
            </Link>
          </aside>
        ) : null}

        <ChatPanel
          sessionId={result.session_id}
          title="Hỏi thêm với thầy"
          emptyHint="Bạn có thể thỉnh giáo thêm để thầy luận sâu hơn theo đúng lá số và lời luận này."
          hideInitialAssistant
          variant="silk"
        />
      </article>
      <ResultDetailDialog detail={detail} result={result} onClose={() => setDetail(null)} />
    </div>
  );
}
